import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";
import {
  X,
  Sparkles,
  CircleDot,
  Check,
} from "lucide-react";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { PREMIUM_BENEFIT_KEYS, PREMIUM_PLANS, PREMIUM_OFFER_PLAN_ID, formatBRL } from "@/config/pricing";
import { useSubscription } from "@/hooks/useSubscription";
import { isPaymentUiBlocked } from "@/lib/platform";
import { analytics } from "@/lib/analytics";
import { metaTrack } from "@/lib/metaPixel";

const PARTICLE_COUNT = 40;

const FloatingParticles: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 2,
      dx: (Math.random() - 0.5) * 0.25,
      dy: -(Math.random() * 0.3 + 0.1),
      hue: Math.random() > 0.5 ? 187 : 270,
      alpha: Math.random() * 0.4 + 0.1,
    }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 72%, 60%, ${p.alpha})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.7 }}
    />
  );
};

const bgShiftKeyframes = `
@keyframes bgShift {
  0% { background-position: 10% 10%; }
  50% { background-position: 90% 90%; }
  100% { background-position: 10% 10%; }
}
`;

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { isPremium, plan } = useSubscription();

  // Paywall placement handed over by PaywallSheet's CTA (navigation state) —
  // stamps checkout_started.source so the funnel survives the route change.
  // Absent (undefined) = organic /pricing visit.
  const paywallSource = (location.state as { paywallPlacement?: string } | null)?.paywallPlacement;

  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    if (isPaymentUiBlocked()) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id);
    });
  }, []);

  useEffect(() => {
    if (!toastMsg) return;
    const timer = setTimeout(() => setToastMsg(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMsg]);

  const isFreePlan = !!userId && plan === "free";

  const handleFreeCta = () => {
    if (isFreePlan) return;
    navigate(userId ? "/home" : "/auth");
  };

  const handlePremiumCta = () => {
    if (isPremium) {
      navigate("/home");
      return;
    }
    analytics.planSelected("premium");
    if (FEATURE_FLAGS.PIX_PAYMENT_ENABLED) {
      analytics.checkoutStarted("premium", paywallSource);
      // Meta mirror — browser event only (CAPI covers signup + Purchase).
      metaTrack("InitiateCheckout", { content_category: "premium", currency: "BRL" });
      // Forward the attribution one more hop so CheckoutPremium can stamp
      // checkout_completed.source when the payment confirms.
      navigate("/checkout/premium", { state: { paywallPlacement: paywallSource } });
    } else {
      setToastMsg(t("pricing.coming_soon"));
    }
  };

  const freeFeatures = [
    "pricing.free.f1",
    "pricing.free.f2",
    "pricing.free.f3",
    "pricing.free.f4",
    "pricing.free.f5",
    "pricing.free.f6",
  ];
  // Fonte única com o Upgrade (22/08): as duas vitrines mostravam listas
  // diferentes sem ninguém ter decidido. Composição/ordem moram em
  // src/config/pricing.ts; o pino em pricingParity.test.ts impede a volta
  // de lista local.
  const premiumFeatures = PREMIUM_BENEFIT_KEYS;
  const premiumPrimary = formatBRL(PREMIUM_PLANS[PREMIUM_OFFER_PLAN_ID].value);

  // Apple 3.1.1 — suppress first-frame flash of the pricing page on iOS
  // native. The useEffect above redirects to /home, but mounting still
  // renders one frame of the full UI before the navigation fires.
  if (isPaymentUiBlocked()) return null;

  return (
    <>
      <style>{bgShiftKeyframes}</style>
      <div
        className="min-h-screen relative overflow-x-hidden"
        style={{ background: "#0a0a0f" }}
      >
        <div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            backgroundImage:
              "radial-gradient(600px circle, hsl(187 72% 15% / 0.3), transparent 70%)",
            backgroundSize: "200% 200%",
            animation: "bgShift 12s ease-in-out infinite",
          }}
        />
        <FloatingParticles />

        <button
          onClick={() => navigate(-1)}
          aria-label={t("common.close")}
          className="fixed right-4 z-50 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <X size={16} className="text-white/60" />
        </button>

        <div
          className="relative z-10 px-5 pb-20 max-w-3xl mx-auto"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3rem)" }}
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles size={18} style={{ color: "hsl(187 72% 55%)" }} />
              <span
                className="text-[10px] font-display tracking-[0.25em] uppercase"
                style={{ color: "hsl(187 72% 50% / 0.7)" }}
              >
                VYV
              </span>
            </div>
            <h1 className="font-display text-[26px] sm:text-[32px] font-bold text-white leading-tight mb-3">
              {t("pricing.headline")}
            </h1>
            <p
              className="text-[14px] font-body max-w-md mx-auto"
              style={{ color: "hsl(210 20% 65%)" }}
            >
              {t("pricing.subtitle")}
            </p>
          </motion.div>

          {/* Tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch mb-10">

            {/* ── FREE ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="relative rounded-2xl p-5 flex flex-col"
              style={{
                background: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1.5px solid rgba(255,255,255,0.08)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <div className="flex items-center gap-2.5 mb-4 mt-1">
                <CircleDot size={20} style={{ color: "rgba(255,255,255,0.4)" }} />
                <span className="font-display text-[15px] font-bold text-white">
                  {t("pricing.free.name")}
                </span>
              </div>
              <div className="mb-5">
                <span className="font-display text-[26px] font-bold text-white">
                  {t("pricing.free.price")}
                </span>
              </div>
              <div className="space-y-2.5 mb-6 flex-1">
                {freeFeatures.map((fk) => (
                  <div key={fk} className="flex items-start gap-2">
                    <Check size={14} className="shrink-0 mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }} />
                    <span
                      className="text-[12px] font-body leading-relaxed"
                      style={{ color: "hsl(210 20% 72%)" }}
                    >
                      {t(fk)}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={handleFreeCta}
                disabled={isFreePlan}
                className="w-full py-3 rounded-xl font-display text-[13px] font-bold tracking-wide transition-all active:scale-[0.97] disabled:cursor-default disabled:active:scale-100"
                style={{
                  background: "transparent",
                  border: "1.5px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                {isFreePlan ? t("pricing.currentPlan") : t("pricing.free.cta")}
              </button>
            </motion.div>

            {/* ── PREMIUM ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative rounded-2xl p-5 flex flex-col"
              style={{
                background: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1.5px solid hsl(187 72% 50% / 0.6)",
                boxShadow:
                  "0 0 40px hsl(187 72% 50% / 0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
            >
              <div
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-display font-bold tracking-wider uppercase whitespace-nowrap"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(187 72% 55%))",
                  color: "#fff",
                  boxShadow: "0 0 16px hsl(187 72% 50% / 0.3)",
                }}
              >
                {t("pricing.popular")}
              </div>

              <div className="flex items-center gap-2.5 mb-4 mt-1">
                <Sparkles size={20} style={{ color: "hsl(187 72% 55%)" }} />
                <span className="font-display text-[15px] font-bold text-white">
                  {t("pricing.core.name")}
                </span>
              </div>
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-[26px] font-bold text-white">
                    {premiumPrimary}
                  </span>
                  <span className="text-[12px] font-body" style={{ color: "hsl(210 20% 65%)" }}>
                    {t("pricing.perMonth")}
                  </span>
                </div>
              </div>
              <div className="space-y-2.5 mb-6 flex-1">
                {premiumFeatures.map((fk) => (
                  <div key={fk} className="flex items-start gap-2">
                    <Check size={14} className="shrink-0 mt-0.5" style={{ color: "hsl(187 72% 55%)" }} />
                    <span
                      className="text-[12px] font-body leading-relaxed"
                      style={{ color: "hsl(210 20% 72%)" }}
                    >
                      {t(fk)}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={handlePremiumCta}
                className="w-full py-3 rounded-xl font-display text-[13px] font-bold tracking-wide transition-all active:scale-[0.97]"
                style={isPremium ? {
                  background: "rgba(255,255,255,0.05)",
                  border: "1.5px solid hsl(187 72% 50% / 0.45)",
                  color: "hsl(187 72% 70%)",
                  cursor: "default",
                } : {
                  background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(187 72% 55%))",
                  border: "none",
                  color: "#fff",
                  boxShadow: "0 0 24px hsl(187 72% 50% / 0.3)",
                }}
              >
                {isPremium ? t("pricing.currentPlan") : t("pricing.core.cta")}
              </button>
            </motion.div>

          </div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-[11px] font-body mb-4"
            style={{ color: "rgba(255,255,255,0.35)" }}
          >
            {t("pricing.cancel")}
          </motion.p>

        </div>

        {/* Toast */}
        <AnimatePresence>
          {toastMsg && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-xl font-body text-[13px] text-white"
              style={{
                background: "rgba(10,10,20,0.92)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(0,229,255,0.20)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                maxWidth: "90vw",
              }}
            >
              {toastMsg}
            </motion.div>
          )}
        </AnimatePresence>

        <Footer variant="minimal" className="relative z-10" />


      </div>
    </>
  );
};

export default Pricing;

